// Display-format helper for non-formula folder columns.
//
// Admins can attach a `prefix`, `suffix`, and `numberFormat` preset to a
// field column so a Jira number field renders as e.g. "R 12,345.67" without
// being wrapped in a formula. The same helper drives the modal's live
// preview chip and the Structure grid's cell rendering, so what an admin
// sees while editing matches what users see in the folder.
//
// Number-format presets:
//   ''               raw pass-through (toString)
//   'int'            Math.round, no thousands separator
//   'int-thousands'  Math.round + 1,234 grouping
//   'dec1'           1,234.5
//   'dec2'           1,234.56
//   'dec3'           1,234.567
//
// Non-numeric values combined with a numberFormat fall back to the raw
// string — partial format (just prefix or suffix) still applies. Empty /
// null values short-circuit so empty cells stay visually empty.

const NUMBER_PRESETS = {
  'int':            { minimumFractionDigits: 0, maximumFractionDigits: 0, useGrouping: false },
  'int-thousands':  { minimumFractionDigits: 0, maximumFractionDigits: 0, useGrouping: true },
  'dec1':           { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: true },
  'dec2':           { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true },
  'dec3':           { minimumFractionDigits: 3, maximumFractionDigits: 3, useGrouping: true },
};

// The default face of a number field when no preset is configured — what
// Jira's list view shows: thousands grouped, the stored fraction kept as is
// ("41,943.2", "2,695", "0"), never padded and never rounded away. Up to
// 2026-09-22 every number column rendered String(value) ("50119.45"), which
// is the one thing a finance column must not look like. Grouping uses the
// en-US separators the aggregate footer already uses, so a column and its
// Σ total read the same. Non-numbers pass through untouched.
export function formatNumberDisplay(value) {
  const num = typeof value === 'number' ? value : Number(value);
  if (typeof value !== 'number' && (typeof value !== 'string' || value.trim() === '')) return value;
  if (!Number.isFinite(num)) return value;
  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 6 });
}

export function formatFieldValue(value, { prefix, suffix, numberFormat } = {}) {
  if (value === null || value === undefined || value === '') return '';

  let body;
  if (numberFormat && NUMBER_PRESETS[numberFormat]) {
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(num)) {
      body = num.toLocaleString(undefined, NUMBER_PRESETS[numberFormat]);
    } else {
      body = String(value);
    }
  } else {
    body = String(value);
  }

  const pre = prefix ? `${prefix} ` : '';
  const suf = suffix ? ` ${suffix}` : '';
  return `${pre}${body}${suf}`;
}
